import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type PropsWithChildren,
} from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  postAuthSignIn,
  type AuthTokens,
  type SignInRequest,
} from '@/entities/auth';
import { usersQueries, type UserMeResponse } from '@/entities/user';
import { tokenManager } from '@/shared/api/token-manager';
import { useCaptureAnalyticsEvent } from '@/shared/analytics/events';
import { useToast } from '@/shared/ui/toast-provider';
import { resolveErrorMessage } from '@/shared/lib/errors';
import { courseQueries } from '@/entities/course';
import { usePostHog } from 'posthog-js/react';

export interface SessionContextValue {
  tokens: AuthTokens | null;
  user: UserMeResponse | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  authError: string | null;
  signIn: (payload: SignInRequest) => Promise<UserMeResponse>;
  signOut: () => Promise<void>;
  clearAuthError: () => void;
  userRefetch: () => Promise<UserMeResponse | null>;
}

const SessionContext = createContext<SessionContextValue | undefined>(
  undefined,
);

export const SessionProvider = ({ children }: PropsWithChildren) => {
  const queryClient = useQueryClient();
  const toast = useToast();
  const posthog = usePostHog();
  const captureAnalyticsEvent = useCaptureAnalyticsEvent();
  const [tokens, setTokens] = useState<AuthTokens | null>(() =>
    tokenManager.getTokens(),
  );
  const [authError, setAuthError] = useState<string | null>(null);

  const resolveUserWithCourseAccess = useCallback(async () => {
    const meQuery = usersQueries.me();
    const user = await queryClient.fetchQuery(meQuery);

    const listQuery = courseQueries.list();
    const courses = await queryClient.fetchQuery(listQuery);

    let userCanTake = user.user_can_take;
    if (Array.isArray(courses)) {
      const activeCourse = courses.find((course) => course.is_active);
      if (activeCourse) {
        userCanTake = activeCourse.user_can_take;
      }
    }

    const enrichedUser: UserMeResponse = {
      ...user,
      user_can_take: userCanTake,
    };

    return enrichedUser;
  }, [queryClient]);

  useEffect(() => {
    const unsubscribe = tokenManager.subscribe(setTokens);
    return unsubscribe;
  }, []);

  useEffect(() => {
    if (!tokens) {
      queryClient.removeQueries({
        queryKey: usersQueries.all,
      });
      return;
    }
  }, [tokens, queryClient]);

  const getTokenMutation = useMutation({
    mutationFn: postAuthSignIn,
  });

  const signIn = useCallback(
    async (payload: SignInRequest) => {
      setAuthError(null);

      try {
        // 1. 로그인 요청하여 토큰 획득
        const nextTokens = await getTokenMutation.mutateAsync(payload);
        setTokens(nextTokens);
        tokenManager.setTokens(nextTokens);

        // 2. 유저 정보 패칭
        const user = await resolveUserWithCourseAccess();

        // 3. 토큰에 유저 정보 추가(성공시만)
        const updatedTokens: AuthTokens = { ...nextTokens, user };
        setTokens(updatedTokens);
        tokenManager.setTokens(updatedTokens);

        // 4. Posthog 이벤트
        captureAnalyticsEvent('auth_sign_in_success');

        // 항상 user 정보 반환 (없으면 null)
        return updatedTokens.user ?? null;
      } catch (error) {
        const message = resolveErrorMessage(
          error,
          'Something went wrong while processing your request.',
        );
        setAuthError(message);
        tokenManager.clearTokens();

        captureAnalyticsEvent('auth_sign_in_failed', {
          reason: message,
        });

        // The sign-in page surfaces this via the inline `authError` message, so
        // we deliberately don't also fire a toast here (no double-surfacing).
        throw error;
      }
    },
    [getTokenMutation, resolveUserWithCourseAccess],
  );

  const signOut = useCallback(async () => {
    tokenManager.clearTokens();
    setAuthError(null);
    queryClient.removeQueries({
      queryKey: usersQueries.all,
    });
    queryClient.removeQueries({
      queryKey: courseQueries.all,
    });
    posthog.reset();
    captureAnalyticsEvent('auth_sign_out');
    toast({
      type: 'info',
      title: 'Signed out',
      description: 'Please return to the sign-in page.',
    });
  }, [queryClient, toast]);

  const userRefetch = useCallback(async () => {
    const latestTokens = tokenManager.getTokens();

    if (!latestTokens?.access) {
      return null;
    }

    try {
      const user = await resolveUserWithCourseAccess();
      const snapshot = tokenManager.getTokens();

      if (!snapshot?.access) {
        return user;
      }

      const tokensWithUser: AuthTokens = {
        ...snapshot,
        user,
      };

      setTokens(tokensWithUser);
      tokenManager.setTokens(tokensWithUser);

      return user;
    } catch (error) {
      tokenManager.clearTokens();
      throw error;
    }
  }, [resolveUserWithCourseAccess, tokens]);

  const clearAuthError = useCallback(() => {
    setAuthError(null);
  }, []);

  const isLoading = getTokenMutation.isPending;

  useEffect(() => {
    if (tokens?.access && tokens.user) {
      posthog.identify(tokens.user.id, {
        email: tokens.user.email,
        name: tokens.user.name,
        license_number: tokens.user.license_number ?? undefined,
      });
    }
  }, [tokens]);

  return (
    <SessionContext.Provider
      value={{
        tokens,
        user: tokens?.user ?? null,
        // Treat stored access tokens as authenticated to avoid beforeLoad timing issues
        isAuthenticated: Boolean(tokens?.access),
        isLoading,
        authError,
        signIn,
        signOut,
        clearAuthError,
        userRefetch,
      }}
    >
      {children}
    </SessionContext.Provider>
  );
};

export const useSession = () => {
  const context = useContext(SessionContext);
  if (!context) {
    throw new Error('useSession must be used within a SessionProvider');
  }

  return context;
};
